import { useMemo } from 'react';
import { StyleSheet } from 'react-native';

import { useRewardTierApplied } from '@fhs/loyalty/src/hooks/use-reward-tier-applied';
import { IconStarRateFill, Text, tokens } from '@fhs/ui';
import { HeaderFrame } from '@fhs/ui';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { useLoyaltyUser } from '@fhs-legacy/frontend/src/state/loyalty/hooks/use-loyalty-user';
import { focusableLabeledA11yProps } from '@fhs-legacy/frontend/src/utils/accessibility/focusable-labeled-props';
import { USER_POINTS_HEADER } from '@fhs-legacy/frontend/src/utils/test-ids';

export default function UserPointsDisplay() {
  const { loyaltyUser } = useLoyaltyUser();
  const { rewardTierApplied } = useRewardTierApplied();
  const incentiveSelections = useAppSelector(selectors.loyalty.selectSelectedOfferSelections);

  // Calculate total points deducted from applied rewards in the cart (reward tier + selections)
  const totalPointsDeducted = useMemo<number>(() => {
    if (!rewardTierApplied?.points || !incentiveSelections?.length) return 0;
    const rewardSelections = incentiveSelections.filter(
      s => s.incentiveId === rewardTierApplied.id
    );
    return rewardSelections.reduce(
      (total, s) => total + rewardTierApplied.points * (s.quantity ?? 1),
      0
    );
  }, [rewardTierApplied, incentiveSelections]);

  // Calculate displayed points (user points minus deducted points)
  const displayedPoints = useMemo(() => {
    if (!loyaltyUser?.points) return 0;
    const userPoints = Number(loyaltyUser.points) || 0;
    return Math.max(0, userPoints - totalPointsDeducted);
  }, [loyaltyUser?.points, totalPointsDeducted]);

  if (!!loyaltyUser?.points && displayedPoints >= 0) {
    const pointsSummary = (() => {
      const formatted = displayedPoints.toLocaleString();
      if (displayedPoints === 1) {
        return totalPointsDeducted > 0
          ? `${formatted} reward point available after items in your cart`
          : `${formatted} reward point`;
      }
      return totalPointsDeducted > 0
        ? `${formatted} reward points available after items in your cart`
        : `${formatted} reward points`;
    })();

    return (
      <HeaderFrame.Right
        style={styles.loyaltyPoints}
        {...focusableLabeledA11yProps(pointsSummary)}
        importantForAccessibility="no-hide-descendants"
      >
        <IconStarRateFill
          accessible={false}
          aria-hidden
          size={24}
          color={tokens.colors.$houseYellow}
        />
        <Text.Heading
          accessible={false}
          importantForAccessibility="no"
          testID={USER_POINTS_HEADER}
          type="five"
        >
          {displayedPoints.toLocaleString()}
        </Text.Heading>
      </HeaderFrame.Right>
    );
  } else {
    return null;
  }
}

const styles = StyleSheet.create({
  loyaltyPoints: {
    alignItems: 'center',
    gap: 4,
  },
});
